/**
 * A square icon button with a text label that lives in the accessibility tree
 * and in the tooltip, but never on screen.
 *
 * Touch targets are 44 px, the size a finger actually needs, even though the
 * glyph inside is 24 — the sketch's airy toolbar survives on a phone that way.
 */

import type { ButtonHTMLAttributes, JSX, ReactNode } from 'react';

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  /** Both the accessible name and the tooltip. */
  label: string;
  icon: ReactNode;
  /** Renders in the pressed state — for toggles. */
  active?: boolean;
}

export function IconButton({ label, icon, active, className, ...rest }: IconButtonProps): JSX.Element {
  const classes = ['icon-button', active ? 'icon-button--active' : '', className ?? '']
    .filter(Boolean)
    .join(' ');
  return (
    <button type="button" className={classes} aria-label={label} title={label} aria-pressed={active} {...rest}>
      {icon}
    </button>
  );
}
